// ── Progression Panel ── End-of-match XP summary overlay.
// Shows XP gained, animated progress bar, level badge, and level-up celebration.
// Displayed after match results are received.

const ACCENT = '#e94560';
const GOLD = '#e8b849';
const BG_DARK = 'rgba(0,0,0,0.92)';
const FONT = '"Courier New", monospace';

// ── XP constants (mirrored from server/internal/game/progression.go) ──
export const XP_PER_LEVEL = 1000;

export interface ProgressionShowData {
  xpGained: number;
  totalXp: number;
  level: number;
  /** Set when the player leveled up this match */
  newLevel?: number;
  /** Optional breakdown */
  breakdown?: XpBreakdown;
}

export interface XpBreakdown {
  kills?: number;
  assists?: number;
  roundsWon?: number;
  mvpStars?: number;
  bombPlants?: number;
  bombDefuses?: number;
  firstBlood?: boolean;
  matchWin?: boolean;
}

export interface MatchXpBreakdown {
  kills: number;
  wins: number;
  mvp: number;
}

export class ProgressionPanel {
  private overlay: HTMLElement;
  private contentEl: HTMLElement;
  private visible = false;
  private boundKeyHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
  private animationFrameId: number | null = null;
  private matchBreakdown: MatchXpBreakdown | null = null;

  constructor(private container: HTMLElement) {
    this.overlay = document.createElement('div');
    Object.assign(this.overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      background: BG_DARK,
      zIndex: '65',
      display: 'none',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONT,
      color: '#ffffff',
      userSelect: 'none',
    });

    this.contentEl = document.createElement('div');
    Object.assign(this.contentEl.style, {
      width: 'min(500px, calc(100vw - 24px))',
      border: `2px solid ${GOLD}40`,
      borderRadius: '8px',
      background: 'rgba(26,26,46,0.98)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    });
    this.overlay.appendChild(this.contentEl);

    // Click outside to close
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.hide();
    });

    document.body.appendChild(this.overlay);
    document.addEventListener('keydown', this.boundKeyHandler);
  }

  show(data: ProgressionShowData): void {
    if (this.visible) this.hide();
    this.visible = true;
    this.overlay.style.display = 'flex';
    this.render(data);
    this.animateXpBar(data);
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.overlay.style.display = 'none';
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  isOpen(): boolean {
    return this.visible;
  }

  setMatchBreakdown(breakdown: Partial<MatchXpBreakdown> | null): void {
    this.matchBreakdown = breakdown
      ? {
          kills: Math.max(0, Math.floor(breakdown.kills ?? 0)),
          wins: Math.max(0, Math.floor(breakdown.wins ?? 0)),
          mvp: Math.max(0, Math.floor(breakdown.mvp ?? 0)),
        }
      : null;
    if (this.visible) this.updateMatchBreakdownWidget();
  }

  dispose(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    document.removeEventListener('keydown', this.boundKeyHandler);
    this.overlay.remove();
    // F-UI-012: Remove the injected level-up keyframes <style> tag and reset
    // the dedup flag so HMR / test reloads don't stack duplicate entries.
    if (levelUpAnimEl) {
      levelUpAnimEl.remove();
      levelUpAnimEl = null;
    }
    levelUpAnimInjected = false;
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.visible) {
      e.preventDefault();
      e.stopPropagation();
      this.hide();
    }
  }

  private render(data: ProgressionShowData): void {
    this.contentEl.innerHTML = '';

    const isLevelUp = data.newLevel !== undefined && data.newLevel > data.level;

    // ── Header ──
    const header = document.createElement('div');
    Object.assign(header.style, {
      padding: '24px 24px 16px',
      borderBottom: `1px solid ${GOLD}30`,
      textAlign: 'center',
    });

    const titleEl = document.createElement('div');
    Object.assign(titleEl.style, {
      fontSize: '20px',
      fontWeight: 'bold',
      letterSpacing: '4px',
      textTransform: 'uppercase',
      color: GOLD,
    });
    titleEl.textContent = 'MATCH RESULTS';
    header.appendChild(titleEl);

    this.contentEl.appendChild(header);

    // ── Level badge ──
    const badgeSection = document.createElement('div');
    Object.assign(badgeSection.style, {
      display: 'flex',
      justifyContent: 'center',
      padding: '24px 0 8px',
    });

    const badge = document.createElement('div');
    Object.assign(badge.style, {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      border: `3px solid ${GOLD}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      background: 'rgba(232,184,73,0.08)',
    });

    const levelNum = document.createElement('div');
    Object.assign(levelNum.style, {
      fontSize: '28px',
      fontWeight: 'bold',
      color: GOLD,
      lineHeight: '1',
    });
    levelNum.textContent = String(isLevelUp ? data.newLevel : data.level);
    badge.appendChild(levelNum);

    const levelLabel = document.createElement('div');
    Object.assign(levelLabel.style, {
      fontSize: '9px',
      color: '#888',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      marginTop: '2px',
    });
    levelLabel.textContent = 'LEVEL';
    badge.appendChild(levelLabel);

    badgeSection.appendChild(badge);
    this.contentEl.appendChild(badgeSection);

    // ── Level-up celebration ──
    if (isLevelUp) {
      const levelUpEl = document.createElement('div');
      levelUpEl.setAttribute('data-testid', 'level-up');
      Object.assign(levelUpEl.style, {
        textAlign: 'center',
        padding: '8px 0',
        fontSize: '18px',
        fontWeight: 'bold',
        color: GOLD,
        letterSpacing: '4px',
        textTransform: 'uppercase',
        animation: 'levelUpPulse 1s ease-in-out infinite',
      });
      levelUpEl.textContent = 'LEVEL UP!';
      this.contentEl.appendChild(levelUpEl);

      // Inject animation keyframes
      this.injectLevelUpAnimation();
    }

    // ── XP gained ──
    const xpGainedEl = document.createElement('div');
    xpGainedEl.setAttribute('data-testid', 'xp-gained');
    Object.assign(xpGainedEl.style, {
      textAlign: 'center',
      padding: '12px 0',
      fontSize: '22px',
      fontWeight: 'bold',
      color: GOLD,
      opacity: '0',
      transition: 'opacity 0.8s ease-in',
    });
    xpGainedEl.textContent = `+${data.xpGained} XP`;
    this.contentEl.appendChild(xpGainedEl);

    // Trigger fade-in on next frame
    requestAnimationFrame(() => {
      xpGainedEl.style.opacity = '1';
    });

    // ── XP progress bar ──
    const barSection = document.createElement('div');
    barSection.setAttribute('data-role', 'xp-bar-section');
    Object.assign(barSection.style, {
      padding: '8px 32px 16px',
    });

    const barLabels = document.createElement('div');
    Object.assign(barLabels.style, {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '10px',
      color: '#666',
      marginBottom: '6px',
    });

    const currentXpLabel = document.createElement('div');
    currentXpLabel.textContent = `${data.totalXp} XP`;
    barLabels.appendChild(currentXpLabel);

    const displayLevel = isLevelUp ? data.newLevel! : data.level;
    const nextLevelXp = (displayLevel + 1) * XP_PER_LEVEL;
    const nextLabel = document.createElement('div');
    nextLabel.textContent = `Level ${displayLevel + 1}: ${nextLevelXp} XP`;
    barLabels.appendChild(nextLabel);
    barSection.appendChild(barLabels);

    const barOuter = document.createElement('div');
    Object.assign(barOuter.style, {
      width: '100%',
      height: '12px',
      background: 'rgba(255,255,255,0.1)',
      borderRadius: '6px',
      overflow: 'hidden',
    });

    const barFill = document.createElement('div');
    barFill.setAttribute('data-testid', 'xp-bar-fill');
    const levelBaseXp = displayLevel * XP_PER_LEVEL;
    const previousXp = data.totalXp - data.xpGained;
    const startPct = XP_PER_LEVEL > 0
      ? Math.max(0, Math.min(100, ((previousXp - levelBaseXp) / XP_PER_LEVEL) * 100))
      : 0;
    Object.assign(barFill.style, {
      width: `${startPct}%`,
      height: '100%',
      background: `linear-gradient(90deg, ${GOLD}, ${ACCENT})`,
      borderRadius: '6px',
      transition: 'width 1.5s ease-out',
    });
    barOuter.appendChild(barFill);
    barSection.appendChild(barOuter);
    this.appendMatchBreakdownWidget(barSection);

    this.contentEl.appendChild(barSection);

    // ── Breakdown ──
    if (data.breakdown) {
      const breakdownSection = document.createElement('div');
      Object.assign(breakdownSection.style, {
        padding: '0 32px 16px',
      });

      const breakdownTitle = document.createElement('div');
      Object.assign(breakdownTitle.style, {
        fontSize: '12px',
        fontWeight: 'bold',
        color: ACCENT,
        letterSpacing: '2px',
        textTransform: 'uppercase',
        marginBottom: '8px',
      });
      breakdownTitle.textContent = 'XP BREAKDOWN';
      breakdownSection.appendChild(breakdownTitle);

      const b = data.breakdown;
      const entries: Array<{ label: string; value: string }> = [];

      if (b.matchWin) entries.push({ label: 'Match Win', value: '+100' });
      if (b.matchWin === false) entries.push({ label: 'Match Loss', value: '+50' });
      if (b.kills) entries.push({ label: `Kills (${b.kills})`, value: `+${b.kills * 10}` });
      if (b.assists) entries.push({ label: `Assists (${b.assists})`, value: `+${b.assists * 5}` });
      if (b.roundsWon) entries.push({ label: `Rounds Won (${b.roundsWon})`, value: `+${b.roundsWon * 5}` });
      if (b.mvpStars) entries.push({ label: `MVP Stars (${b.mvpStars})`, value: `+${b.mvpStars * 50}` });
      if (b.bombPlants) entries.push({ label: `Bomb Plants (${b.bombPlants})`, value: `+${b.bombPlants * 25}` });
      if (b.bombDefuses) entries.push({ label: `Bomb Defuses (${b.bombDefuses})`, value: `+${b.bombDefuses * 25}` });
      if (b.firstBlood) entries.push({ label: 'First Blood', value: '+20' });

      for (const entry of entries) {
        const row = document.createElement('div');
        Object.assign(row.style, {
          display: 'flex',
          justifyContent: 'space-between',
          padding: '4px 0',
          fontSize: '12px',
          borderBottom: '1px solid rgba(255,255,255,0.04)',
        });

        const label = document.createElement('div');
        label.style.color = '#888';
        label.textContent = entry.label;
        row.appendChild(label);

        const value = document.createElement('div');
        value.style.color = GOLD;
        value.style.fontWeight = 'bold';
        value.textContent = entry.value;
        row.appendChild(value);

        breakdownSection.appendChild(row);
      }

      this.contentEl.appendChild(breakdownSection);
    }

    this.contentEl.appendChild(this.createNextMilestoneLine(displayLevel));

    // ── Footer ──
    const footer = document.createElement('div');
    Object.assign(footer.style, {
      padding: '14px',
      textAlign: 'center',
      fontSize: '11px',
      color: '#444',
      borderTop: '1px solid rgba(255,255,255,0.05)',
    });
    footer.textContent = '[ESC] Close';
    this.contentEl.appendChild(footer);
  }

  private createNextMilestoneLine(currentLevel: number): HTMLElement {
    const nextRank = currentLevel + 1;
    const nextXp = nextRank * XP_PER_LEVEL;
    const milestone = document.createElement('div');
    Object.assign(milestone.style, {
      padding: '0 32px 16px',
      color: '#888',
      fontSize: '12px',
      letterSpacing: '1px',
      textAlign: 'center',
    });
    milestone.textContent = `Next milestone: Rank ${nextRank} (${nextXp} XP)`;
    return milestone;
  }

  private animateXpBar(data: ProgressionShowData): void {
    const barFill = this.contentEl.querySelector('[data-testid="xp-bar-fill"]') as HTMLElement | null;
    if (!barFill) return;

    const isLevelUp = data.newLevel !== undefined && data.newLevel > data.level;
    const displayLevel = isLevelUp ? data.newLevel! : data.level;
    const levelBaseXp = displayLevel * XP_PER_LEVEL;
    const currentPct = XP_PER_LEVEL > 0
      ? Math.max(0, Math.min(100, ((data.totalXp - levelBaseXp) / XP_PER_LEVEL) * 100))
      : 0;

    if (isLevelUp) {
      let fired = false;
      const fireOnce = () => {
        if (fired || !this.visible || !this.contentEl.contains(barFill)) return;
        fired = true;
        this.fireLevelUpConfetti();
      };
      barFill.addEventListener('transitionend', (e) => {
        if (e.propertyName === 'width') fireOnce();
      }, { once: true });
      globalThis.setTimeout(fireOnce, 1600);
    }

    // Animate after a short delay
    this.animationFrameId = requestAnimationFrame(() => {
      barFill.style.width = `${currentPct}%`;
      this.animationFrameId = null;
    });
  }

  private updateMatchBreakdownWidget(): void {
    const barSection = this.contentEl.querySelector('[data-role="xp-bar-section"]') as HTMLElement | null;
    if (!barSection) return;
    this.appendMatchBreakdownWidget(barSection);
  }

  private appendMatchBreakdownWidget(barSection: HTMLElement): void {
    const existing = barSection.querySelector('[data-testid="match-xp-breakdown"]');
    existing?.remove();
    if (!this.matchBreakdown) return;

    const b = this.matchBreakdown;
    const widget = document.createElement('div');
    widget.setAttribute('data-testid', 'match-xp-breakdown');
    Object.assign(widget.style, {
      marginTop: '8px',
      padding: '6px 8px',
      borderRadius: '4px',
      background: 'rgba(255,255,255,0.05)',
      color: '#cfd2d8',
      fontSize: '11px',
      letterSpacing: '0.5px',
      textAlign: 'center',
    });
    widget.textContent = `Kills: +${b.kills} | Wins: +${b.wins} | MVP: +${b.mvp}`;
    barSection.appendChild(widget);
  }

  private fireLevelUpConfetti(): void {
    this.injectLevelUpAnimation();

    const host = document.createElement('div');
    Object.assign(host.style, {
      position: 'absolute',
      left: '50%',
      top: '42%',
      width: '1px',
      height: '1px',
      pointerEvents: 'none',
      zIndex: '2',
    });

    const colors = [GOLD, ACCENT, '#4caf50', '#2196f3', '#ff9800', '#ffffff', '#00ced1', '#c77dff'];
    for (let i = 0; i < 8; i++) {
      const particle = document.createElement('span');
      const angle = Math.random() * Math.PI * 2;
      const distance = 48 + Math.random() * 46;
      particle.style.setProperty('--tx', `${Math.cos(angle) * distance}px`);
      particle.style.setProperty('--ty', `${Math.sin(angle) * distance}px`);
      particle.style.setProperty('--rot', `${Math.round(Math.random() * 540)}deg`);
      Object.assign(particle.style, {
        position: 'absolute',
        width: '6px',
        height: '10px',
        borderRadius: '2px',
        background: colors[i],
        left: '0',
        top: '0',
        animation: 'levelUpConfetti 1s ease-out forwards',
      });
      host.appendChild(particle);
    }

    this.contentEl.appendChild(host);
    globalThis.setTimeout(() => host.remove(), 1000);
  }

  private injectLevelUpAnimation(): void {
    // Module-level dedup so re-creating a panel instance does not inject the
    // keyframes a second time (instance-level flag would reset on recreate).
    if (levelUpAnimInjected) return;
    levelUpAnimInjected = true;

    const style = document.createElement('style');
    style.textContent = `
      @keyframes levelUpPulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.7; transform: scale(1.1); }
      }
      @keyframes levelUpConfetti {
        0% { opacity: 1; transform: translate(-50%, -50%) scale(1) rotate(0deg); }
        100% { opacity: 0; transform: translate(calc(-50% + var(--tx)), calc(-50% + var(--ty))) scale(0.4) rotate(var(--rot)); }
      }
    `;
    document.head?.appendChild(style);
    // F-UI-012: Track the injected style element so dispose() / test reset
    // can remove it; otherwise HMR + test reloads stack duplicates.
    levelUpAnimEl = style;
  }
}

let levelUpAnimInjected = false;
let levelUpAnimEl: HTMLStyleElement | null = null;

/** F-UI-012: Test-only helper — removes the cached <style> tag and resets
 *  the dedup flag so test isolation can re-inject the keyframes. */
export function _resetLevelUpAnimationForTest(): void {
  if (levelUpAnimEl) {
    levelUpAnimEl.remove();
    levelUpAnimEl = null;
  }
  levelUpAnimInjected = false;
}
